import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

// Static fallback — used when nav_items table is empty or unreachable.
// group field drives the 3-column mega menu layout in Navbar.
const STATIC_NAV_ITEMS = [
  // ── Top-level ─────────────────────────────────────────────────────────────
  { id: 1, label: "Solutions",   href: "/services",    location: "header", parent_id: null, sort_order: 0 },
  { id: 2, label: "Work",        href: "/work",        location: "header", parent_id: null, sort_order: 1 },
  { id: 3, label: "Products",    href: "/products",    location: "header", parent_id: null, sort_order: 2 },
  { id: 4, label: "Industries",  href: "/industries",  location: "header", parent_id: null, sort_order: 3 },
  { id: 5, label: "About",       href: "/about",       location: "header", parent_id: null, sort_order: 4 },

  // ── Web & Digital ─────────────────────────────────────────────────────────
  { id: 101, label: "Web Development",     href: "/services/web-development",   location: "header", parent_id: 1, sort_order: 0,  group: "Web & Digital" },
  { id: 102, label: "WordPress",           href: "/services/wordpress",         location: "header", parent_id: 1, sort_order: 1,  group: "Web & Digital" },
  { id: 103, label: "Shopify / E-Commerce",href: "/services/shopify",           location: "header", parent_id: 1, sort_order: 2,  group: "Web & Digital" },
  { id: 104, label: "Web Apps",            href: "/services/web-apps",          location: "header", parent_id: 1, sort_order: 3,  group: "Web & Digital" },
  { id: 105, label: "UI/UX Design",        href: "/services/ui-ux-design",      location: "header", parent_id: 1, sort_order: 4,  group: "Web & Digital" },
  { id: 106, label: "Branding",            href: "/services/branding",          location: "header", parent_id: 1, sort_order: 5,  group: "Web & Digital" },
  { id: 107, label: "Graphic Design",      href: "/services/graphic-design",    location: "header", parent_id: 1, sort_order: 6,  group: "Web & Digital" },
  { id: 108, label: "Content Writing",     href: "/services/content-writing",   location: "header", parent_id: 1, sort_order: 7,  group: "Web & Digital" },
  { id: 109, label: "SEO Services",        href: "/services/seo-services",      location: "header", parent_id: 1, sort_order: 8,  group: "Web & Digital" },
  { id: 110, label: "Digital Marketing",   href: "/services/digital-marketing", location: "header", parent_id: 1, sort_order: 9,  group: "Web & Digital" },
  { id: 111, label: "Social Media",        href: "/services/social-media",      location: "header", parent_id: 1, sort_order: 10, group: "Web & Digital" },
  { id: 112, label: "Brand Strategy",      href: "/services/brand-strategy",    location: "header", parent_id: 1, sort_order: 11, group: "Web & Digital" },

  // ── Software & Enterprise ─────────────────────────────────────────────────
  { id: 201, label: "CRM Development",     href: "/services/crm-development",   location: "header", parent_id: 1, sort_order: 0,  group: "Software & Enterprise" },
  { id: 202, label: "ERP Development",     href: "/services/erp-development",   location: "header", parent_id: 1, sort_order: 1,  group: "Software & Enterprise" },
  { id: 203, label: "Custom Software",     href: "/services/custom-software",   location: "header", parent_id: 1, sort_order: 2,  group: "Software & Enterprise" },
  { id: 204, label: "App Development",     href: "/services/app-development",   location: "header", parent_id: 1, sort_order: 3,  group: "Software & Enterprise" },
  { id: 205, label: "Mobile Apps",         href: "/services/mobile-apps",       location: "header", parent_id: 1, sort_order: 4,  group: "Software & Enterprise" },
  { id: 206, label: "API Development",     href: "/services/api-development",   location: "header", parent_id: 1, sort_order: 5,  group: "Software & Enterprise" },
  { id: 207, label: "Cloud & DevOps",      href: "/services/cloud-devops",      location: "header", parent_id: 1, sort_order: 6,  group: "Software & Enterprise" },
  { id: 208, label: "Integrations",        href: "/services/integrations",      location: "header", parent_id: 1, sort_order: 7,  group: "Software & Enterprise" },
  { id: 209, label: "Analytics",           href: "/services/analytics",         location: "header", parent_id: 1, sort_order: 8,  group: "Software & Enterprise" },

  // ── AI & Automation ───────────────────────────────────────────────────────
  { id: 301, label: "AI Agents & Chatbots", href: "/services/ai-agents",        location: "header", parent_id: 1, sort_order: 0,  group: "AI & Automation" },
  { id: 302, label: "AI Call Centre",       href: "/services/ai-call-centre",   location: "header", parent_id: 1, sort_order: 1,  group: "AI & Automation" },
  { id: 303, label: "LLM Integration",      href: "/services/llm-integration",  location: "header", parent_id: 1, sort_order: 2,  group: "AI & Automation" },
  { id: 304, label: "RAG Pipelines",        href: "/services/rag-pipelines",    location: "header", parent_id: 1, sort_order: 3,  group: "AI & Automation" },
  { id: 305, label: "RPA Automation",       href: "/services/rpa-automation",   location: "header", parent_id: 1, sort_order: 4,  group: "AI & Automation" },
  { id: 306, label: "AI Consulting",        href: "/services/ai-consulting",    location: "header", parent_id: 1, sort_order: 5,  group: "AI & Automation" },
  { id: 307, label: "Creative Services",    href: "/services/creative-services",location: "header", parent_id: 1, sort_order: 6,  group: "AI & Automation" },
];

interface CMSSettingsContextProps {
  settings: Record<string, string>;
  navItems: any[];
  integrations: Record<string, any>;
  loading: boolean;
  error: string | null;
}

const CMSSettingsContext = createContext<CMSSettingsContextProps | undefined>(undefined);

export const CMSSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [navItems, setNavItems] = useState<any[]>([]);
  const [integrations, setIntegrations] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [
          { data: settingsData, error: settingsError },
          { data: navData, error: navError },
          { data: intData, error: intError }
        ] = await Promise.all([
          supabase.from('settings').select('*'),
          supabase.from('nav_items').select('*'),
          supabase.from('integrations').select('*')
        ]);

        if (settingsError) throw settingsError;
        if (navError) throw navError;
        if (intError) throw intError;

        if (settingsData) {
          const settingsObj = settingsData.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
          setSettings(settingsObj);
        }
        // Only use DB nav if it has a proper parent-child hierarchy.
        // If the table has rows but no children (parent_id set), the mega-menu
        // won't work — fall back to the fully-structured static data instead.
        const dbHasChildren = navData?.some((n: any) => n.parent_id != null);
        if (navData?.length && dbHasChildren) setNavItems(navData);
        else setNavItems(STATIC_NAV_ITEMS);
        if (intData) {
          const intObj = intData.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.config_json || curr.value }), {});
          setIntegrations(intObj);
        }

      } catch (err: any) {
        console.error('Failed to load CMS context:', err);
        setError(err.message);
        setNavItems(STATIC_NAV_ITEMS);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  return (
    <CMSSettingsContext.Provider value={{ settings, navItems, integrations, loading, error }}>
      {children}
    </CMSSettingsContext.Provider>
  );
};

export const useCMSSettings = () => {
  const context = useContext(CMSSettingsContext);
  if (!context) {
    throw new Error('useCMSSettings must be used within a CMSSettingsProvider');
  }
  return context;
};
